<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main.vue'
import LayoutFilter from '@/components/layout/layout-filter.vue'
import { LayoutToggle } from '@/shared/constants/layout/LayoutToggle.js'
import CandidateZoom from '@components/candidate/candidate-zoom.vue'
import BookingDateStrip from '@components/booking/booking-date-strip.vue'
import ReplaceMeBanner from '@/components/replaceMe/replace-me-banner.vue'
import BookingSummaryEmptyDayBlock from '@/components/booking/booking-summary-empty-day-block.vue'
import { mapActions, mapGetters } from 'vuex'
import { orderBy } from 'lodash'
import dayjs from '@/services/date'
import baseMixin from '@/mixins/baseMixin'
import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import TheDashboardNavigation from '@/components/layout/the-dashboard-navigation.vue'
import CancelBookingDialog from '@components/booking/cancel-booking-dialog.vue'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import OverviewStatsDto from '@/models/overview/overviewStatsDto'
import DashboardFillRateStats from '@/components/booking/dashboard-fill-rate-stats.vue'
import ClientFillRateFilter from '@/components/booking/client-fill-rate-filter.vue'
import FillRateFilterType from '@/shared/constants/booking/FillRateFilterType'
import clientGroupOverviewFeatureMixin from '@/mixins/clientGroupOverviewFeatureMixin'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import BookingSummaryCardList from '@/components/booking/summary/booking-summary-card-list.vue'
import BookingSummaryList from '@/components/booking/summary/booking-summary-list.vue'

export default {
  name: 'HomeView',
  components: {
    Layout,
    LayoutFilter,
    CandidateZoom,
    BookingDateStrip,
    ReplaceMeBanner,
    BookingSummaryEmptyDayBlock,
    TheDashboardNavigation,
    CancelBookingDialog,
    BookingSummaryList,
    DashboardFillRateStats,
    ClientFillRateFilter,
    BookingSummaryCardList,
  },
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: appConfig.description }],
    }
  },
  mixins: [baseMixin, clientGroupOverviewFeatureMixin],
  data() {
    return {
      isLoading: false,
      layout: LayoutToggle.TILE,
      arrayEvents: [],
      picker: '2021-08-20',
      selectedDay: dayjs(),
      selectedMonthYear: null,
      showCandidateModal: false,
      selectedCandidate: null,
      bookingSummary: {},
      bookingList: [],
      refreshSummaryData: false,
      defaultTempBookingsForLoader: 5,
      bookingToCancel: null,
      showCancelBookingDialog: false,
      fillRateFilterType: FillRateFilterType.selectedMonth,
      stats: null,
      expandAll: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/currentUser',
      hasCreateBookingPermissionForAtleastOneClientLocation:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocation',
      getClient: 'auth/getClient',
      getClients: 'auth/getClients',
      getSelectedClients: 'client/getSelectedClients',
      getPermission: 'auth/getPermission',
      getNodeHierarchyByIdAndLevel: 'auth/getNodeHierarchyByIdAndLevel',
      getAllClientsLocationsWithPermission:
        'auth/getAllClientsLocationsWithPermission',
      bookingOverview: 'bookings/bookingOverview',
      isLoadingBookingSummary: 'bookings/isLoadingBookingSummary',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingPermission: 'auth/hasLocationBookingPermission',
      hasLocationPendingApprovalPermission:
        'auth/hasLocationPendingApprovalPermission',
      hasClientBookingPermission: 'auth/hasClientBookingPermission',
      hasClientPendingApprovalPermission:
        'auth/hasClientPendingApprovalPermission',
      hasPermissionRegardlessOfLevel: 'auth/hasPermissionRegardlessOfLevel',
    }),
    compBookings() {
      // Check if the array is empty or if the array is full of numbers.
      // The array will only contain numbers while loading to display skeleton
      // loader cards.
      if (isNullOrEmptyArray(this.bookingList) || !isNaN(this.bookingList[0]))
        return []

      const clientLocationsWithBookings = []

      for (const client of this.bookingList) {
        const tempClientLocationsWithBookings = {
          clientId: client.clientId,
          clientName: client.clientName,
          locations: [],
        }

        for (const location of client.locations) {
          if (isNullOrEmptyArray(location.bookings)) continue

          for (const booking of location.bookings) {
            if (!booking?.blockDetails) continue

            booking.blockText = this.$t('booking.blockRangeText', {
              bookingPosition: booking.blockDetails.bookingPosition,
              totalCount: booking.blockDetails.bookingsCount,
            })

            booking.blockTooltipText = this.$t('booking.blockTooltipText', {
              startDate: dayjs(booking.blockDetails.startDateLocal)
                .locale(this.$i18n.locale.toLowerCase())
                .format('L'),
              endDate: dayjs(booking.blockDetails.endDateLocal)
                .locale(this.$i18n.locale.toLowerCase())
                .format('L'),
            })
          }

          tempClientLocationsWithBookings.locations.push(location)
        }

        // If no locations were mapped (i.e. no locations had any bookings)
        // don't add client or locations to list
        if (isNullOrEmptyArray(tempClientLocationsWithBookings.locations))
          continue

        clientLocationsWithBookings.push(tempClientLocationsWithBookings)
      }

      return clientLocationsWithBookings
    },
    canViewBookings() {
      return (
        this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
          this.getSelectedClients
        ) ||
        this.hasPermissionRegardlessOfLevel(
          PermissionScope.PENDING_BOOKING,
          PermissionModifier.VIEW
        ) ||
        this.hasPermissionRegardlessOfLevel(
          PermissionScope.PENDING_BOOKING,
          PermissionModifier.CREATE
        )
      )
    },
    isLoadingSummaryOrOverviewData() {
      return this.isLoading || this.isLoadingBookingSummary
    },
    isReplaceMeEnabled() {
      return this.replaceMeEnabled(this.getSelectedClients[0]) // TODO: Rework to use multiple clients
    },
    isTileLayout() {
      return this.layout === LayoutToggle.TILE
    },
    compTitle() {
      if (this.getSelectedClients.length > 1) {
        return this.$t('home.viewMultipleClientsTitle', [
          this.getSelectedClients.length,
        ])
      }

      const client = this.getClient(this.getSelectedClients[0])
      return client?.clientName || 'Unknown'
    },
    displayRandomSubtitleGreeting() {
      // Randomised Greetings
      const quotes = [
        `Hi ${this.user?.firstName?.trim() || ''}, hope you are well`,
        'Glad to have you back!',
        'Welcome back! We missed you!',
        `Hope you're having a great ${dayjs()
          .local()
          .format('dddd')}`,
      ]

      return quotes[Math.floor(Math.random() * quotes.length)]
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    selectedDayHasBooking() {
      return this.compBookings && this.compBookings?.length > 0
    },
    /**
     * Returns a count for how many skeleton loaders should be generated for
     * a specific date. It will throttle the amount of loaders generated if the
     * limit is exceeded.
     */
    compBookingSummarySkeletonLoaderCount() {
      if (!this.isLoadingSummaryOrOverviewData) return 0

      const SKELTON_LOADER_LIMIT = 10
      return this.bookingList.length > SKELTON_LOADER_LIMIT
        ? SKELTON_LOADER_LIMIT
        : this.bookingList.length
    },
    compExpandBtnText() {
      return this.expandAll ? 'Collapse All' : 'Expand All'
    },
  },
  watch: {
    fillRateFilterType() {
      this.initFillRateStats()
    },
    selectedDay() {
      this.determineExpandAllState()
      this.initFillRateStats()
    },
    async selectedMonthYear() {
      await this.$nextTick()
      if (this.fillRateFilterType !== FillRateFilterType.selectedMonth) return

      this.initFillRateStats()
    },
    /**
     * Listen for changes to the selected clients and refresh the data based on
     * new selection
     */
    async getSelectedClients() {
      if (!this.canViewBookings) return

      await this.refreshDashboardBookingData()

      this.initFillRateStats()
    },
  },
  async created() {
    // Don't load booking summary if user doesn't have access to bookings
    if (!this.canViewBookings) return

    await this.initBookingSummaryData()

    this.initFillRateStats()
  },
  mounted() {
    if (!this.canViewBookings) return
    this.arrayEvents = [...Array(6)].map(() => {
      const day = Math.floor(Math.random() * 30)
      const d = new Date()
      d.setDate(day)
      return d.toISOString().substr(0, 10)
    })
  },
  methods: {
    ...mapActions({
      loadBookingsByClientAndDate: 'bookings/loadBookingsByClientAndDate',
      getOverviewSummaryBookingViewModels:
        'bookings/getOverviewSummaryBookingViewModels',
    }),
    determineExpandAllState() {
      // Will expand all panels if there are 5 or less clients being displayed
      this.expandAll = this.bookingList.length <= 5
    },
    getComparisonDate() {
      switch (this.fillRateFilterType) {
        case FillRateFilterType.selectedDate:
          return this.selectedDay
        case FillRateFilterType.selectedMonth: {
          // If selectedMonthYear isn't set, likely this is on page load, which
          // starts off with today's date and month (which is selectedDay's default)
          if (!this.selectedMonthYear) return this.selectedDay
          return this.selectedMonthYear
        }
      }
    },
    fillRateDateComprisonFunc(bookingVM) {
      const formatKey =
        this.fillRateFilterType === FillRateFilterType.selectedMonth
          ? DateFormatToken.YYYYMM
          : DateFormatToken.YYYYMMDD

      const comparisonDate = this.getComparisonDate()

      const selectedDate = dayjs(comparisonDate).format(formatKey)

      const bookingDate = dayjs(bookingVM.dateLocal).format(formatKey)

      return bookingDate === selectedDate
    },
    calcFillRateStats() {
      const stats = new OverviewStatsDto({})

      if (isNullOrEmptyArray(this.bookingOverview)) return stats

      const bookings = this.bookingOverview.filter(
        this.fillRateDateComprisonFunc
      )

      if (isNullOrEmptyArray(bookings)) return stats

      const result = bookings.reduce((accumulator, currentValue) => {
        accumulator.totalBookings += currentValue.totalBookingsCount
        accumulator.unfilled += currentValue.openBookingsCount
        accumulator.filled = accumulator.totalBookings - accumulator.unfilled
        return accumulator
      }, stats)

      return new OverviewStatsDto({
        openBookings: result.unfilled,
        totalBookings: result.totalBookings,
      })
    },
    initFillRateStats() {
      this.stats = this.calcFillRateStats()
    },
    generateFillRateFilterOptions() {
      return [
        {
          id: 'selected-month',
          text: this.$t('home.bookings.monthFilterLabelText', [
            dayjs(this.getComparisonDate()).format(DateFormatToken.MMMMYYYY),
          ]),
          tooltip: this.$t('home.bookings.monthFilter'),
        },
        {
          id: 'selected-day',
          text: dayjs(this.selectedDay)
            .locale(this.$i18n.locale)
            .format(DateFormatToken.ll),
          tooltip: this.$t('home.bookings.dateFilter'),
        },
      ]
    },
    orderBookings(bookingData) {
      if (isNullOrEmptyArray(bookingData)) return []

      const clients = this.getClients(this.getSelectedClients)

      if (isNullOrEmptyArray(clients)) return []

      const clientsAndLocationsWithPerms = []

      const mapBookingsToLocation = (location) => ({
        locationId: location.locationId,
        locationName: location.locationName,
        bookings: orderBy(
          bookingData?.filter(
            (booking) => booking?.location?.id === location.locationId
          ),
          ['candidate.firstName', 'candidate.lastName'],
          ['asc', 'asc']
        ),
      })

      for (const client of clients) {
        const tempClientWithLocations = {
          clientId: client.clientId,
          clientName: client.clientName,
          locations: [],
        }

        for (const location of client.locations) {
          tempClientWithLocations.locations.push(
            mapBookingsToLocation(location)
          )
        }

        if (!isNullOrEmptyArray(tempClientWithLocations.locations))
          clientsAndLocationsWithPerms.push(tempClientWithLocations)
      }

      return orderBy(clientsAndLocationsWithPerms, ['clientName'], ['asc'])
    },
    async initBookingSummaryData(initPickerDate = dayjs()) {
      this.picker = dayjs(initPickerDate).format('YYYY-MM-DD')

      this.isLoading = true

      this.fillWithTemp(this.defaultTempBookingsForLoader)

      const dayjsObject = dayjs(this.picker)

      const result = await this.loadBookingsByClientAndDate(
        dayjsObject.format('YYYY-MM-DD')
      )

      this.bookingList = this.orderBookings(result.data)
      this.isLoading = false
    },
    changeLayout(layout) {
      this.determineExpandAllState()
      this.layout = layout
    },
    /**
     * Fills booking list with elements so its length matches nItems
     * @param {Number} nItems
     */
    fillWithTemp(nItems) {
      this.bookingList = Array.from(Array(nItems).keys())
    },
    async changeDay(dateObject, nItems) {
      this.selectedDay = null
      await this.$nextTick()
      this.selectedDay = dayjs(dateObject)

      // If user selected the same date as currently selected, do nothing, otherwise load bookings
      if (this.picker === dateObject) return

      this.picker = dateObject

      this.isLoading = true

      this.fillWithTemp(nItems)

      const dayjsObject = dayjs(dateObject)

      // If summary tells us that there aren't any bookings on that date then skip loading them
      if (nItems === 0) {
        this.isLoading = false
        this.bookingList = []
        return
      }

      const result = await this.loadBookingsByClientAndDate(
        dayjsObject.format(DateFormatToken.YYYYMMDD)
      )

      this.bookingList = this.orderBookings(result.data)
      this.isLoading = false
    },
    async viewTodaysBookings() {
      await this.changeDay(dayjs(), this.defaultTempBookingsForLoader) // arbitrary number since we won't know how many bookings there are in the parent
    },
    async refreshDashboardBookingData() {
      this.refreshSummaryData = true // Refresh datestrip booking data
      await this.initBookingSummaryData(this.picker) // Set date to be same as the currently selected date
    },
    showCandidateDialog(candidatePrefill) {
      this.selectedCandidate = candidatePrefill
      this.showCandidateModal = true
    },
    closeCandidateDialog() {
      this.selectedCandidate = null
      this.showCandidateModal = false
    },
    openCancelBookingDialog(booking) {
      this.bookingToCancel = booking
      this.showCancelBookingDialog = true
    },
    closeCancelBookingDialog() {
      this.bookingToCancel = null
      this.showCancelBookingDialog = false
    },
    async closeCancelBookingDialogAndRefresh() {
      this.closeCancelBookingDialog()
      await this.refreshDashboardBookingData()
    },
    filterChanged(value) {
      this.fillRateFilterType = value
    },
    monthChanged(value) {
      this.selectedMonthYear = value
    },
    yearChanged(value) {
      this.selectedMonthYear = value
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="[isMediumOrLower ? '' : 'd-flex justify-space-between']">
      <BasePageTitle
        :title="compTitle"
        :subtitle="displayRandomSubtitleGreeting"
        subtitle-on-top
      />

      <div
        v-if="clientGroupOverviewFeatureToggles.canViewDetails"
        :class="[
          isMediumOrLower ? 'align-start mb-4' : 'align-center',
          'd-flex flex-column',
        ]"
      >
        <DashboardFillRateStats v-if="stats" :stats="stats" />
        <ClientFillRateFilter
          :filter="fillRateFilterType"
          :options="generateFillRateFilterOptions()"
          @filterChanged="filterChanged"
        />
      </div>
    </div>
    <v-row>
      <v-col
        cols="12"
        md="12"
        lg="12"
        :class="isMobileViewPort ? 'px-0 mx-0' : ''"
      >
        <ReplaceMeBanner @refreshData="refreshDashboardBookingData" />
        <v-sheet
          v-if="canViewBookings"
          class="transparent"
          elevation="0"
          :class="isMobileViewPort ? 'px-0 mx-0' : ''"
        >
          <div :class="isMobileViewPort ? 'px-0 mx-0' : ''">
            <BookingDateStrip
              :refresh-data="refreshSummaryData"
              :selected-day="selectedDay"
              @refreshComplete="refreshSummaryData = false"
              @dateSelected="changeDay"
              @monthChanged="monthChanged"
              @yearChanged="yearChanged"
            />

            <div v-if="selectedDayHasBooking || isLoadingSummaryOrOverviewData">
              <div class="my-4 d-flex justify-space-between align-center">
                <LayoutFilter
                  :pre-select="layout"
                  @layoutChanged="changeLayout"
                />
                <v-btn
                  color="primary"
                  text
                  outlined
                  @click="expandAll = !expandAll"
                  ><v-icon left>{{
                    expandAll ? 'mdi-collapse-all' : 'mdi-expand-all'
                  }}</v-icon>
                  {{ compExpandBtnText }}</v-btn
                >
              </div>

              <BookingSummaryCardList
                v-if="isTileLayout"
                :bookings="compBookings"
                :loading-count="compBookingSummarySkeletonLoaderCount"
                :expand-all="expandAll"
                @viewCandidate="showCandidateDialog"
                @cancelBooking="openCancelBookingDialog"
              />
              <BookingSummaryList
                v-else
                :bookings="compBookings"
                :loading-count="compBookingSummarySkeletonLoaderCount"
                :expand-all="expandAll"
                @cancelBooking="openCancelBookingDialog"
              />
            </div>
            <BookingSummaryEmptyDayBlock
              v-else-if="selectedDay"
              :selected-date="selectedDay"
              @viewTodaysBookings="viewTodaysBookings"
            />
          </div>
        </v-sheet>
        <TheDashboardNavigation v-else />
      </v-col>
    </v-row>
    <v-dialog
      v-if="showCandidateModal"
      v-model="showCandidateModal"
      width="720px"
      content-class="rounded-xl"
    >
      <CandidateZoom
        :candidate-prefill="selectedCandidate"
        @closeModal="closeCandidateDialog"
      />
    </v-dialog>
    <CancelBookingDialog
      v-if="showCancelBookingDialog"
      :show="showCancelBookingDialog"
      :booking="bookingToCancel"
      @closeModal="closeCancelBookingDialog"
      @closeModalAndRefresh="closeCancelBookingDialogAndRefresh"
    />
  </Layout>
</template>
